import React, { memo } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RecentlyViewedItem } from '@/types/residents/marketplaceListing';
import { SectionHeader } from '../SectionHeader';

interface MarketplaceRecentlyViewedProps {
  items: RecentlyViewedItem[];
  onItemPress: (item: RecentlyViewedItem) => void;
  onClear: () => void;
  thumbnailSize?: { width: number; height: number };
}

const THUMBNAIL_WIDTH = 110;
const THUMBNAIL_HEIGHT = 112;
const THUMBNAIL_GAP = 12;

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

export const MarketplaceRecentlyViewed: React.FC<MarketplaceRecentlyViewedProps> = memo(({
  items,
  onItemPress,
  onClear,
  thumbnailSize,
}) => {
  if (items.length === 0) return null;

  const thumbW = thumbnailSize?.width ?? THUMBNAIL_WIDTH;
  const thumbH = thumbnailSize?.height ?? THUMBNAIL_HEIGHT;

  return (
    <View>
      <SectionHeader title="Recently Viewed" actionLabel="Clear" onAction={onClear} />
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {items.map((item) => (
          <RecentlyViewedThumbnail
            key={item.id}
            item={item}
            onPress={() => onItemPress(item)}
            width={thumbW}
            height={thumbH}
          />
        ))}
      </ScrollView>
    </View>
  );
});

MarketplaceRecentlyViewed.displayName = 'MarketplaceRecentlyViewed';

const RecentlyViewedThumbnail: React.FC<{
  item: RecentlyViewedItem;
  onPress: () => void;
  width: number;
  height: number;
}> = memo(({ item, onPress, width, height }) => {
  const { colors } = useThemeColors();
  const hasImage = !!item.image;

  return (
    <TouchableOpacity
      style={[styles.thumbnail, { width, height, borderColor: colors.card.border }]}
      onPress={onPress}
      activeOpacity={0.8}
    >
      {hasImage ? (
        <Image
          source={{ uri: item.image }}
          style={styles.thumbnailImage}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.placeholderContainer, { backgroundColor: colors.card.bg }]}>
          <PropertyPlaceholder width={width * 0.6} height={height * 0.6} />
        </View>
      )}
      <View style={styles.priceBadge}>
        <Text style={styles.priceText}>{item.listing.price}</Text>
      </View>
    </TouchableOpacity>
  );
});

RecentlyViewedThumbnail.displayName = 'RecentlyViewedThumbnail';

const styles = StyleSheet.create({
  scrollContent: {
    gap: THUMBNAIL_GAP,
  },
  thumbnail: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  placeholderContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  priceBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  priceText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
